import { getExtraProperty } from '@tribeplatform/gql-client/lib'
import { ExtraPropertyEntityType } from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  useUpsertExtraProperty,
} from '@tribeplatform/react-sdk/hooks'
import { ActionPanel } from '@tribeplatform/react-ui-kit/ActionPanel'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

export const SupportAccess = () => {
  const { $t } = useI18n()

  const { data: member, isModerator } = useAuthMember()
  const { mutateAsync: upsertExtraProperty, isLoading } =
    useUpsertExtraProperty()

  const hasSupportAccess = !!getExtraProperty(
    member?.extraProperties,
    'supportAccess',
  )

  if (!isModerator) {
    return null
  }

  return (
    <ActionPanel
      title={
        <div className="flex space-s-2 items-center">
          <span>
            <T defaultMessage="Support access" id="Settings.SupportAccess" />
          </span>
          <Tooltip>
            <Tooltip.Trigger className="mt-1">
              <SvgIcon className="text-content-subdued" name="eye-off" />
            </Tooltip.Trigger>
            <Tooltip.Panel>
              <T
                defaultMessage="This option is only visible to community admins and moderators"
                id="Settings.SupportAccess.Tooltip"
              />
            </Tooltip.Panel>
          </Tooltip>
        </div>
      }
      description={
        hasSupportAccess ? (
          <T
            defaultMessage="You have granted Bettermode access to your account for support purposes. You can revoke access at any time."
            id="Settings.SupportAccess.RevokeDescription"
          />
        ) : (
          <T
            defaultMessage="Grant Bettermode support access to your account so we can troubleshoot and fix problems on your behalf. You can revoke access at any time."
            id="Settings.SupportAccess.GrantDescription"
          />
        )
      }
      placement="trailing-top"
    >
      <Button
        size="lg"
        variant="secondaryNeutral"
        loading={isLoading}
        onClick={async () => {
          try {
            await upsertExtraProperty({
              input: {
                entityType: ExtraPropertyEntityType.Member,
                entityId: member?.id,
                key: 'supportAccess',
                value: JSON.stringify(!hasSupportAccess ? Date.now() : false),
              },
            })
          } catch (err) {
            logger.error('Error updating supportAccess extraProperty')
            toast({
              title: $t({
                defaultMessage: 'Something went wrong',
                id: 'Generics.SomethingWentWrong',
              }),
              description: $t({
                id: 'Generics.TryAgainLater',
                defaultMessage: 'Please try again later.',
              }),
              status: 'error',
            })
          }
        }}
      >
        {hasSupportAccess ? (
          <T
            defaultMessage="Revoke access"
            id="Settings.SupportAccess.RevokeAccess"
          />
        ) : (
          <T
            defaultMessage="Allow access"
            id="Settings.SupportAccess.AllowSupportAccess"
          />
        )}
      </Button>
    </ActionPanel>
  )
}
